import { Component, OnInit, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { CardModule } from 'primeng/card';
import { MessageModule } from 'primeng/message';
import { PanelModule } from 'primeng/panel';
import { AuthService } from '../../services/auth.service';
import { LoginRequest } from '../../models/user.model';
import { IMC_AUTH_CONFIG } from '../../auth.config';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    PasswordModule,
    CardModule,
    MessageModule,
    PanelModule
  ],
  template: `
    <div class="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-blue-100 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <!-- Loading Overlay for Azure AD Processing -->
      <div *ngIf="isAzureLoading || isAzureProcessing" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div class="bg-white rounded-lg p-8 shadow-2xl max-w-sm mx-4 text-center">
          <div class="mb-4">
            <i class="pi pi-spin pi-spinner text-blue-500 text-5xl"></i>
          </div>
          <h3 class="text-xl font-semibold text-gray-900 mb-2">
            {{ isAzureProcessing ? 'Verifying Credentials' : 'Redirecting to Microsoft' }}
          </h3>
          <p class="text-gray-600 mb-4">
            {{ isAzureProcessing
              ? 'Please wait while we verify your credentials and set up your session...'
              : 'Please wait while we redirect you to sign in with your account...'
            }}
          </p>
          <div class="flex items-center justify-center space-x-2 text-sm text-gray-500">
            <i class="pi pi-shield text-green-500"></i>
            <span>Secure Authentication in Progress</span>
          </div>
        </div>
      </div>

      <div class="max-w-md w-full space-y-8">
        <div class="text-center">
          <div class="mx-auto w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-6 shadow-lg">
            <i class="pi pi-chart-line text-white text-2xl"></i>
          </div>
          <h2 class="text-center text-4xl font-bold text-blue-800 mb-2">
            {{ title }}
          </h2>
          <p class="text-gray-600 text-base font-medium">
            {{ subtitle }}
          </p>
        </div>

        <p-card class="shadow-2xl border-0 rounded-2xl overflow-hidden">
          <div class="space-y-6">
            <!-- Error and Session Messages -->
            <div *ngIf="errorMessage" class="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
              <div class="flex items-center">
                <i class="pi pi-times-circle mr-2"></i>
                <span>{{ errorMessage }}</span>
              </div>
            </div>

            <div *ngIf="sessionExpiredMessage" class="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg">
              <div class="flex items-center">
                <i class="pi pi-clock mr-2"></i>
                <span>{{ sessionExpiredMessage }}</span>
              </div>
            </div>

            <!-- Azure AD / Microsoft 365 Sign In Button (Primary) -->
            <div *ngIf="showAzureAD">
              <p-button
                type="button"
                (click)="onAzureADLogin()"
                [loading]="isAzureLoading"
                [disabled]="isAzureLoading || isLoading"
                styleClass="w-full"
                size="large"
                severity="primary"
              >
                <ng-template pTemplate="content">
                  <span class="flex items-center justify-center gap-3">
                    <svg
                      *ngIf="!isAzureLoading"
                      class="w-5 h-5"
                      viewBox="0 0 23 23"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="11" height="11" fill="#F25022" />
                      <rect y="12" width="11" height="11" fill="#00A4EF" />
                      <rect x="12" width="11" height="11" fill="#7FBA00" />
                      <rect x="12" y="12" width="11" height="11" fill="#FFB900" />
                    </svg>
                    <span class="font-semibold">{{
                      isAzureLoading
                        ? 'Signing in...'
                        : azureButtonText
                    }}</span>
                  </span>
                </ng-template>
              </p-button>
            </div>

            <!-- Divider -->
            <div *ngIf="showAzureAD && showEmailPassword" class="relative my-6">
              <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-gray-300"></div>
              </div>
              <div class="relative flex justify-center text-sm">
                <span class="px-4 bg-white text-gray-500 font-medium">
                  Or use email and password
                </span>
              </div>
            </div>

            <!-- Email/Password Form -->
            <div *ngIf="showEmailPassword">
              <p-panel
                *ngIf="showAzureAD"
                [toggleable]="true"
                [collapsed]="emailPasswordCollapsed"
                (collapsedChange)="emailPasswordCollapsed = !!$event"
              >
                <ng-template pTemplate="header">
                  <span class="text-sm font-semibold text-gray-700">
                    <i class="pi pi-envelope mr-2"></i>
                    Sign in with Email & Password
                  </span>
                </ng-template>

                <ng-container *ngTemplateOutlet="emailPasswordForm"></ng-container>
              </p-panel>

              <ng-container *ngIf="!showAzureAD">
                <ng-container *ngTemplateOutlet="emailPasswordForm"></ng-container>
              </ng-container>
            </div>
          </div>
        </p-card>

        <!-- Footer -->
        <footer *ngIf="showFooter" class="mt-8 text-center">
          <div class="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-4 text-sm text-gray-600">
            <span>{{ footerText }}</span>
            <span *ngIf="version" class="hidden sm:inline text-gray-400">|</span>
            <div *ngIf="version" class="flex items-center space-x-2">
              <span class="text-gray-500">Version:</span>
              <span class="font-mono bg-gray-100 px-2 py-1 rounded text-xs text-gray-800">
                {{ version }}
              </span>
            </div>
          </div>
        </footer>
      </div>
    </div>

    <!-- Email/Password Form Template -->
    <ng-template #emailPasswordForm>
      <form [formGroup]="loginForm" (ngSubmit)="onSubmit()" class="space-y-4 pt-2">
        <div class="flex flex-col gap-2">
          <label for="email" class="font-medium text-gray-700">
            <i class="pi pi-envelope mr-2 text-blue-500"></i>Email Address
          </label>
          <input
            id="email"
            name="email"
            type="email"
            pInputText
            formControlName="email"
            placeholder="Enter your email address"
            class="w-full"
          />
          <small
            *ngIf="loginForm.get('email')?.invalid && loginForm.get('email')?.touched"
            class="text-red-600"
          >
            <i class="pi pi-exclamation-triangle mr-1"></i>
            Please enter a valid email address
          </small>
        </div>

        <div class="flex flex-col gap-2">
          <label for="password" class="font-medium text-gray-700">
            <i class="pi pi-key mr-2 text-blue-500"></i>Password
          </label>
          <p-password
            id="password"
            name="password"
            formControlName="password"
            [feedback]="false"
            [toggleMask]="true"
            placeholder="Enter your password"
            styleClass="w-full"
            inputStyleClass="w-full"
          ></p-password>
          <small
            *ngIf="loginForm.get('password')?.invalid && loginForm.get('password')?.touched"
            class="text-red-600"
          >
            <i class="pi pi-exclamation-triangle mr-1"></i>
            Password is required
          </small>
        </div>

        <div class="pt-2">
          <p-button
            type="submit"
            [label]="submitButtonText"
            [loading]="isLoading"
            loadingIcon="pi pi-spinner pi-spin"
            [disabled]="loginForm.invalid || isLoading"
            styleClass="w-full"
            size="large"
            severity="primary"
          >
            <ng-template pTemplate="content">
              <span class="flex items-center justify-center gap-2">
                <i class="pi pi-sign-in" *ngIf="!isLoading"></i>
                <span>{{ isLoading ? 'Signing In...' : submitButtonText }}</span>
              </span>
            </ng-template>
          </p-button>
        </div>
      </form>
    </ng-template>
  `
})
export class LoginComponent implements OnInit {
  @Input() title = 'Call Avoidance Search';
  @Input() subtitle = 'Sign in to access the dashboard';
  @Input() showAzureAD = true;
  @Input() showEmailPassword = true;
  @Input() azureButtonText = 'Sign in with Microsoft 365';
  @Input() submitButtonText = 'Sign In';
  @Input() showFooter = true;
  @Input() footerText = 'InfoCision Management Corporation';
  @Input() version = '';
  @Input() successRoute = '/dashboard';
  @Input() requireAdmin = false;

  @Output() loginSuccess = new EventEmitter<void>();
  @Output() loginError = new EventEmitter<string>();

  loginForm: FormGroup;
  isLoading = false;
  isAzureLoading = false;
  isAzureProcessing = false;
  errorMessage = '';
  sessionExpiredMessage = '';
  emailPasswordCollapsed = true;

  private authService = inject(AuthService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private config = inject(IMC_AUTH_CONFIG);

  constructor(private fb: FormBuilder) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]]
    });

    this.showAzureAD = !!this.config.azureAD;
    this.requireAdmin = this.config.requireAdmin || false;
  }

  ngOnInit(): void {
    this.authService.azureRedirectProcessing$.subscribe(isProcessing => {
      this.isAzureProcessing = isProcessing;
      console.log('[Login] Azure redirect processing state changed:', isProcessing);

      if (!isProcessing && this.authService.isAuthenticated()) {
        console.log('[Login] User authenticated and processing complete, redirecting');
        this.loginSuccess.emit();
        this.router.navigate([this.successRoute]);
      }
    });

    this.activatedRoute.queryParams.subscribe(params => {
      if (params['expired'] === 'true') {
        this.sessionExpiredMessage = 'Your session has expired due to inactivity. Please sign in again.';
      }

      if (params['error'] === 'azure_ad_failed') {
        this.errorMessage = 'Microsoft sign-in failed. Please try again or use email/password login.';
      } else if (params['error'] === 'azure_ad_error') {
        this.errorMessage = 'An error occurred during Microsoft sign-in. Please try again.';
      } else if (params['error'] === 'not_admin') {
        this.errorMessage = 'Access denied. Admin privileges required.';
      }
    });

    if (!this.isAzureProcessing && this.authService.isAuthenticated()) {
      console.log('[Login] User already authenticated, redirecting');
      this.router.navigate([this.successRoute]);
    }
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';
      this.sessionExpiredMessage = '';

      const credentials: LoginRequest = this.loginForm.value;

      this.authService.login(credentials).subscribe({
        next: (response) => {
          if (this.requireAdmin && !response.user.isAdmin) {
            this.errorMessage = 'Access denied. Admin privileges required.';
            this.isLoading = false;
            this.loginError.emit(this.errorMessage);
          } else {
            this.loginSuccess.emit();
            this.router.navigate([this.successRoute]);
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error.error?.error || 'Login failed. Please try again.';
          this.loginError.emit(this.errorMessage);
        }
      });
    }
  }

  onAzureADLogin(): void {
    this.isAzureLoading = true;
    this.errorMessage = '';
    this.sessionExpiredMessage = '';

    try {
      this.authService.loginWithAzureAD().subscribe();
    } catch (error) {
      this.isAzureLoading = false;
      console.error('[Azure AD] Login error:', error);
      this.errorMessage = 'Microsoft sign-in failed. Please try again or use email/password login.';
      this.loginError.emit(this.errorMessage);
    }
  }
}
